import { Link } from 'react-router-dom';
import { History, X } from 'lucide-react';
import { useRecentlyViewed } from '@/hooks/useRecentlyViewed';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const pricingColors: Record<string, string> = {
  free: 'bg-success text-success-foreground',
  freemium: 'bg-warning text-warning-foreground',
  paid: 'bg-primary text-primary-foreground',
  enterprise: 'bg-secondary text-secondary-foreground'
};

export function RecentlyViewed() {
  const { recentTools, clearHistory } = useRecentlyViewed();

  if (recentTools.length === 0) {
    return null;
  }

  return (
    <section className="container mx-auto px-4 pb-12">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <History className="h-5 w-5 text-muted-foreground" />
          <h2 className="font-display text-xl font-semibold">Recently Viewed</h2>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={clearHistory}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4 mr-1" />
          Clear
        </Button>
      </div>
      
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
        {recentTools.map((tool, index) => (
          <Link 
            key={tool.id} 
            to={`/tool/${tool.slug}`}
            className="shrink-0 animate-fade-in"
            style={{ animationDelay: `${index * 50}ms` }}
          >
            <div className="group bg-card border rounded-lg p-3 hover:border-primary/50 transition-all w-56 hover-scale">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                  {tool.logo_url ? (
                    <img src={tool.logo_url} alt={tool.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-lg font-bold text-muted-foreground">
                      {tool.name.charAt(0)}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm truncate group-hover:text-primary transition-colors">
                    {tool.name}
                  </h3>
                  <Badge className={cn("text-[10px] px-1.5 py-0", pricingColors[tool.pricing])}>
                    {tool.pricing}
                  </Badge>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2 line-clamp-1">
                {tool.short_description}
              </p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
